/**
 * OSPROD-BOOK followup: customer-facing cancel surface at
 * /booking/cancel/:token. Mailed to the customer in the
 * confirmation email so they can cancel without logging into our
 * app.
 *
 * Three states:
 *   - loading the appointment
 *   - active appointment, asks for confirmation, then POSTs cancel
 *   - already cancelled (or just-cancelled): friendly receipt
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Check, X } from 'lucide-react'
import { publicBookingApi, type PublicBookingDetails } from '../services/api'

export default function PublicBookingCancelPage() {
  const { token } = useParams<{ token: string }>()
  const [details, setDetails] = useState<PublicBookingDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState<'just-cancelled' | 'already-cancelled' | null>(null)

  useEffect(() => {
    if (!token) return
    publicBookingApi.getCancelDetails(token)
      .then((res) => {
        setDetails(res.data)
        if (res.data.status === 'CANCELLED') setDone('already-cancelled')
      })
      .catch((err) => setError(err?.response?.data?.error || 'Booking not found'))
  }, [token])

  const handleCancel = async () => {
    if (!token) return
    setSubmitting(true)
    try {
      const res = await publicBookingApi.cancel(token)
      setDone(res.data.alreadyCancelled ? 'already-cancelled' : 'just-cancelled')
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Could not cancel — try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (error) {
    return (
      <CenteredLayout>
        <div className="w-12 h-12 rounded-full bg-destructive/15 text-destructive flex items-center justify-center mx-auto mb-4">
          <X size={22} />
        </div>
        <div className="text-foreground text-xl font-semibold mb-2">Can't load this booking</div>
        <p className="text-muted-foreground text-sm">{error}</p>
      </CenteredLayout>
    )
  }

  if (!details) {
    return <CenteredLayout><div className="text-muted-foreground text-sm">Loading…</div></CenteredLayout>
  }

  const ownerName = [details.link.owner.firstName, details.link.owner.lastName]
    .filter(Boolean).join(' ') || details.link.owner.username
  const whenInOwnerZone = new Date(details.startsAt).toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: details.link.timezone,
    timeZoneName: 'short',
  })
  const whenInLocalZone = new Date(details.startsAt).toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
  })

  if (done === 'just-cancelled' || done === 'already-cancelled') {
    return (
      <CenteredLayout>
        <div className="w-12 h-12 rounded-full bg-green-500/15 text-green-500 flex items-center justify-center mx-auto mb-4">
          <Check size={22} />
        </div>
        <div className="text-foreground text-xl font-semibold mb-2">
          {done === 'already-cancelled' ? 'Already cancelled' : 'Booking cancelled'}
        </div>
        <p className="text-muted-foreground text-sm">
          Your {whenInLocalZone} booking with {ownerName} is no longer on the calendar.
        </p>
      </CenteredLayout>
    )
  }

  return (
    <CenteredLayout>
      <h1 className="text-foreground text-xl font-semibold mb-1">Cancel this booking?</h1>
      <p className="text-muted-foreground text-sm mb-4">
        {details.link.title} with {ownerName}
      </p>

      <div className="bg-muted rounded-lg px-4 py-3 mb-4 text-left">
        <div className="text-[11px] uppercase text-muted-foreground tracking-wide mb-1">Your local time</div>
        <div className="text-[14px] text-foreground font-medium">{whenInLocalZone}</div>
        <div className="text-[11px] uppercase text-muted-foreground tracking-wide mt-2 mb-1">{ownerName}'s time</div>
        <div className="text-[13px] text-muted-foreground">{whenInOwnerZone}</div>
      </div>

      <button
        type="button"
        onClick={handleCancel}
        disabled={submitting}
        className="w-full px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-[13px] font-medium hover:bg-destructive/90 transition disabled:opacity-50"
      >
        {submitting ? 'Cancelling…' : 'Yes, cancel it'}
      </button>
      <p className="text-[11.5px] text-muted-foreground mt-3">
        Changed your mind? Just close this page — nothing happens until you click.
      </p>
    </CenteredLayout>
  )
}

function CenteredLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md text-center">{children}</div>
    </div>
  )
}
